import { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { INumberDictionary, IStore } from '../../interfaces';
import { IAvailableCompetition, ICenterOfCountry, ICompetitionEvolution, ITeam } from '../../interfaces/clientInterfaces';
import { useSelector } from 'react-redux';
import * as Leaflet from 'leaflet';
import { StyledHeader } from './Header';
import { renderToStaticMarkup } from 'react-dom/server';
import * as actions from '../../actions';
import { useAppDispatch } from '../../utils/modules';
import { Teams } from '../../utils/teams';

//#region styled Components
const ParticipantsMapContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	overflow-y: hidden;
	width: 100%;
`;
const MapContainer = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
	.shield_divIconConatiner {
		background-color: transparent;
		border: none;
	}
`;

const ButtonsContainer = styled.div`
	display: flex;
	flex-direction: row;
	margin: 5px auto;
`;

interface IStyledFilterHeader {
	isSelected: boolean;
}
const Button = styled(StyledHeader)<IStyledFilterHeader>`
	cursor: pointer;
	margin: 0 10px;
	font-size: 12px;
	background-color: ${props => props.isSelected ? '#DAA520' : '#000'};
	color: ${props => props.isSelected ? '#000' : '#DAA520'};
`;

interface IDivIconImage {
	isFlag: boolean;
}
const ShieldMarkerCircleContainer = styled.div<IDivIconImage>`
	position: relative;
	display: flex;
	#shield_CircleContainer {
		max-width: ${props => props.isFlag ? 40 : 60}px !important;
		max-height: ${props => props.isFlag ? 30 : 40}px !important;
	}
`;

const ShieldMarkerCircle = styled.div`
	height: 20px;
	width: 20px;
	border-radius: 40px;
	position: relative;
	background-color: #DAA520;
	margin: auto 0 0 0;
	display: flex;
	left: -10px;
`;

const RunnerUpShieldMarkerCircle = styled(ShieldMarkerCircle)`
	background-color: #6c7a86;
`;

const BronzeMedalShieldMarkerCircle = styled(ShieldMarkerCircle)`
	background-color: #cd3233;
	color: #000;	
`;

const ShieldMarkerNumberContainer = styled.div`
	font-family: 'Roddenberry';
	margin: auto;
	position: relative;
	text-align: center;
	font-size: 15px;
	color: black;
	line-height: 20px;
`;

const PARTICIPANTS_MAP_ID = 'PARTICIPANTS_MAP_ID';


interface ICompetitionEvolutionInjectedProps {
	availableCompetitions: IAvailableCompetition[];
	isMobileViewActive: boolean;
	evolutions: INumberDictionary<ICompetitionEvolution>;
	teamsById: INumberDictionary<ITeam>;
	competitionName: string;
}

enum MapSection {
	participants = 'participants',
	champions = 'champions',
	finalists = 'finalists'
}

export default function Finalists() {

	const dispatch = useAppDispatch();
	
	const {
		availableCompetitions,
		isMobileViewActive,
		evolutions,
		teamsById,
		competitionName
	} = useSelector(
		(completeState: IStore): ICompetitionEvolutionInjectedProps => {
			return {
				availableCompetitions: completeState.competitions.availableCompetitions,
				isMobileViewActive: completeState.ui.isMobileViewActive,
				evolutions: completeState.competitions.evolutions,
				teamsById: completeState.teams.teamsById,
				competitionName: completeState.competitions.selectedEvolutions[0]?.competitionName || ''
			}
		});
	
	const stadiumCluster = useSelector(
		(completeState: IStore): INumberDictionary<number[]> => {
			return completeState.map.stadiumCluster;
		});
	
	const [mapSection, setMapSection] = useState(MapSection.finalists);

	const clickParticipantsSection = useCallback(
		() => {
			if (mapSection !== MapSection.participants) {
				setMapSection(MapSection.participants)
			}
		},
		[mapSection]
	);

	const clickChampionsSection = useCallback(
		() => {
			if (mapSection !== MapSection.champions) {
				setMapSection(MapSection.champions)
			}
		},
		[mapSection]
	);

	const clickFinalistsSection = useCallback(
		() => {
			if (mapSection !== MapSection.finalists) {
				setMapSection(MapSection.finalists)
			}
		},
		[mapSection]
	);

	const competition = useMemo(
		(): IAvailableCompetition | undefined => {
			return availableCompetitions.find(competition => competition.name === competitionName);
		},
		[competitionName, availableCompetitions]
	);

	interface IChampionTeamMarker {
		team: ITeam;
		timesChampions: number;
		timesRunnerUps: number;
		timesBronze: number;
	}

	const getMarkerIcon = useCallback(
		(
			team: ITeam,
			timesChampions: number | undefined,
			timesRunnerUp: number | undefined,
			timesBronze: number | undefined
		)=> {
			if (mapSection === MapSection.champions && !timesChampions) {
				return null;
			}
			return (
				<ShieldMarkerCircleContainer
					isFlag={!team.teamCode}
					title={team.name}
				>
					<img
						id={'shield_CircleContainer'}
						src={team.teamCode ? `../../../img/shield/${team.countryCode}_${team.teamCode}.png` : `../../../img/flag/${team.countryCode}.png`}
						alt={team.name}
					/>
					{
						timesChampions || (!team.teamCode && mapSection === MapSection.finalists)
							? <ShieldMarkerCircle>
								<ShieldMarkerNumberContainer>{timesChampions || 0}</ShieldMarkerNumberContainer>
							</ShieldMarkerCircle>
							: null
					}
					{
						timesRunnerUp || (!team.teamCode && mapSection === MapSection.finalists)
							? <RunnerUpShieldMarkerCircle>
								<ShieldMarkerNumberContainer>{timesRunnerUp || 0}</ShieldMarkerNumberContainer>
							</RunnerUpShieldMarkerCircle>
							: null
					}
					{
						timesBronze || (!team.teamCode && mapSection === MapSection.finalists)
							? <BronzeMedalShieldMarkerCircle>
								<ShieldMarkerNumberContainer>{timesBronze || 0}</ShieldMarkerNumberContainer>
							</BronzeMedalShieldMarkerCircle>
							: null
					}
				</ShieldMarkerCircleContainer>
			);
		},
		[mapSection]
	)

	const { participantTeams, championTeams } = useMemo(
		(): {
			participantTeams: ITeam[];
			championTeams: IChampionTeamMarker[];
		} => {
			if (!competition) {
				return {
					participantTeams: [],
					championTeams: []
				};
			}

			const teamsOfCompetition = 'undefined';
			if (typeof teamsOfCompetition === 'undefined') {
				setMapSection(MapSection.champions);
			}
			let championTeamsDict: INumberDictionary<IChampionTeamMarker> = {};
			const evolutionOfCompetition = evolutions[competition.id];
			if (evolutionOfCompetition && evolutionOfCompetition.editions.length > 0) {
				const champions = evolutionOfCompetition.editions[evolutionOfCompetition.editions.length - 1].champions;
				for (const key of Object.keys(champions)) {
					const keyId = Number.parseInt(key);
					if (!Number.isNaN(keyId)) {
						const team = teamsById[keyId];
						if (team) {
							championTeamsDict = {
								...championTeamsDict,
								[team.teamId]: {
									team,
									timesChampions: champions[keyId]?.length || 0,
									timesRunnerUps: 0,
									timesBronze: 0
								}
							}
						}
					}
				}
				const runnerUps = evolutionOfCompetition.editions[evolutionOfCompetition.editions.length - 1].runnerUps;
				for (const key of Object.keys(runnerUps)) {
					const keyId = Number.parseInt(key);
					if (!Number.isNaN(keyId)) {
						const team = teamsById[keyId];
						if (team) {
							const championsTeam = championTeamsDict[team.teamId];
							if (championsTeam) {
								championTeamsDict = {
									...championTeamsDict,
									[team.teamId]: {
										...championsTeam,
										timesRunnerUps: runnerUps[keyId]?.length || 0
									}
								}
							} else {
								championTeamsDict = {
									...championTeamsDict,
									[team.teamId]: {
										team,
										timesChampions: 0,
										timesRunnerUps: runnerUps[keyId]?.length || 0,
										timesBronze: 0
									}
								}
							}
						}
					}
				}
			}
			return {
				participantTeams: [],
				championTeams: Object.values(championTeamsDict)
			};
		},
		[competition, evolutions, teamsById]
	);

	const setTeamMarker = useCallback(
		(
			team: ITeam,
			numberToRender: number | undefined,
			numberToRender2: number | undefined,
			numberToRender3: number | undefined,
		) => {
			const teamMarker = getMarkerIcon(team, numberToRender, numberToRender2, numberToRender3);
			if (!teamMarker || !team.stadium) {
				return null;
			}
			const iconAnchor = Teams.getIconAnchorInStadiumCluster(team, stadiumCluster);
			const divIcon = new Leaflet.divIcon({
				className: 'shield_divIconConatiner',
				html: renderToStaticMarkup(teamMarker),
				iconSize: ['auto', 'auto'],
				iconAnchor,
			});
			return Leaflet.marker(
				[team.stadium.north, team.stadium.east],
				{
					icon: divIcon,
					id_test: team.teamId,
					riseOnHover: true
				})
		},
		[getMarkerIcon, stadiumCluster]
	);

	const renderTeamsInMap = useCallback(
		() => {
			const center: ICenterOfCountry = {
				center: [0, 0],
				zoom: 5,
				mobileZoom: 5
			};
	
			const zoom = isMobileViewActive ? center.mobileZoom : center.zoom;
			const shieldsMap = Leaflet.map(PARTICIPANTS_MAP_ID).setView(center.center, zoom);
			Leaflet.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
				attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
				maxZoom: 10,
				id: 'mapbox/streets-v11',
				tileSize: 512,
				zoomOffset: -1,
				accessToken: 'pk.eyJ1IjoiYWdhcmNhaCIsImEiOiJjazhieG50NnYwMDRvM2VwNmZjZHd1aDBzIn0.GKhS7ajz4tM51I_pplBlKA'
			}).addTo(shieldsMap);
	
			
			const markers = [];
			if (mapSection === MapSection.participants) {
				for (const team of participantTeams) {
					const teamMarker = setTeamMarker(team, undefined, undefined, undefined);
					if (teamMarker) {
						markers.push(teamMarker);
						teamMarker.addTo(shieldsMap)
					}
				}
			} else if (mapSection === MapSection.champions) {
				for (const championTeam of championTeams) {
					const teamMarker = setTeamMarker(championTeam.team, championTeam.timesChampions, 0, undefined);
					if (teamMarker) {
						markers.push(teamMarker);
						teamMarker.addTo(shieldsMap).on('click', () => {
							dispatch(actions.setHomeDetailTeam(championTeam.team.teamId));
						});
					}
				}
			} else if (mapSection === MapSection.finalists) {
				for (const championTeam of championTeams) {
					const teamMarker = setTeamMarker(championTeam.team, championTeam.timesChampions, championTeam.timesRunnerUps, false ? championTeam.timesBronze : undefined);
					if (teamMarker) {
						markers.push(teamMarker);
						teamMarker.addTo(shieldsMap).on('click', () => {
							dispatch(actions.setHomeDetailTeam(championTeam.team.teamId));
						});
					}
				}
			}
			if (markers.length > 0) {
				const group = new Leaflet.featureGroup(markers);
				shieldsMap.fitBounds(group.getBounds());
			}
		},
		[championTeams, isMobileViewActive, mapSection, participantTeams, setTeamMarker, dispatch]
	);

	useEffect(() => {
		const container = Leaflet.DomUtil.get(PARTICIPANTS_MAP_ID);
     	if (container != null){
        	container._leaflet_id = null;
     	 }
		renderTeamsInMap();
	}, [mapSection, competitionName, renderTeamsInMap]);

	if (!competition) {
		return null;
	}
	return (
		<ParticipantsMapContainer>
			<ButtonsContainer>
				{participantTeams.length > 0
					? <Button isSelected={MapSection.participants === mapSection} onClick={clickParticipantsSection}>
						{'CURRENT TEAMS'}
					</Button> : null
				}
				<Button isSelected={MapSection.champions === mapSection} onClick={clickChampionsSection}>
					{'Champions'}
				</Button>
				<Button isSelected={MapSection.finalists === mapSection} onClick={clickFinalistsSection}>
					{'Runner ups'}
				</Button>
			</ButtonsContainer>
			<MapContainer id={PARTICIPANTS_MAP_ID}/>
		</ParticipantsMapContainer>
	);
}
